/**
 * MessagesPanel
 * Real-time message thread between travel agent and user
 * Sends via WhatsApp (Olli WABA) or email fallback
 * Receives user replies via SSE
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './MessagesPanel.css';

const API_BASE = '/api';

const MessagesPanel = ({ applicationId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [, setConversationId] = useState(null);
  const [channel, setChannel] = useState('whatsapp');
  const [hasWhatsapp, setHasWhatsapp] = useState(false);
  const [windowActive, setWindowActive] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const lastMsgCountRef = useRef(0);

  // Load existing thread
  const loadThread = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/conversations/${applicationId}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        const msgs = data.data.messages || [];
        setMessages(msgs);
        lastMsgCountRef.current = msgs.length;
        setConversationId(data.data.conversationId);
        setChannel(data.data.channel || 'whatsapp');
        setHasWhatsapp(data.data.hasWhatsapp);
        setWindowActive(data.data.windowActive || false);
      }
    } catch (err) {
      console.error('[MessagesPanel] loadThread error:', err);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // Scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Build a fingerprint to detect any change (new messages, updated media_url, status)
  const msgsFingerprintRef = useRef('');

  // Poll for new/updated messages every 3 seconds
  useEffect(() => {
    const buildFingerprint = (msgs) =>
      msgs.map(m => `${m.id}:${m.media_url || ''}:${m.status || ''}`).join('|');

    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/conversations/${applicationId}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success) {
          const msgs = data.data.messages || [];
          const newFingerprint = buildFingerprint(msgs);
          // Update if anything changed (new messages, media_url updates, status changes)
          if (newFingerprint !== msgsFingerprintRef.current) {
            setMessages(msgs);
            lastMsgCountRef.current = msgs.length;
            msgsFingerprintRef.current = newFingerprint;
            setWindowActive(data.data.windowActive || false);
          }
        }
      } catch (_) {}
    }, 3000);

    return () => clearInterval(poll);
  }, [applicationId]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    setSendError('');

    const optimistic = {
      id: Date.now(),
      direction: 'agent_to_user',
      message_type: 'text',
      content: text.trim(),
      status: 'sending',
      sent_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, optimistic]);
    const sentText = text.trim();
    setText('');

    try {
      const res = await fetch(`${API_BASE}/conversations/${applicationId}/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: sentText }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to send');

      // Update optimistic message status
      setMessages(prev => prev.map(m =>
        m.id === optimistic.id ? { ...m, status: 'sent', id: data.data?.messageId || m.id } : m
      ));
    } catch (err) {
      setSendError(err.message || 'Failed to send message');
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="messages-panel">
      {/* Header */}
      <div className="messages-header">
        <div className="messages-header-left">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span className="messages-header-title">Messages</span>
        </div>
        <button className="messages-close-btn" onClick={onClose} title="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Thread */}
      <div className="messages-thread">
        {loading ? (
          <div className="messages-loading">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="messages-empty">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{opacity: 0.3}}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p>No messages yet.</p>
            <p className="messages-empty-hint">Type a message below to contact the applicant.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`message-bubble ${msg.direction === 'agent_to_user' ? 'bubble-out' : 'bubble-in'}`}
            >
              {msg.media_url ? (
                <div className="message-media">
                  {msg.media_url.match(/\.(jpg|jpeg|png|gif|webp)/i) || msg.media_url.includes('/api/gateway/documents/') ? (
                    <img src={msg.media_url} alt="attachment" className="message-media-img" />
                  ) : (
                    <div className="message-media-file">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <span>{msg.media_filename || 'Attachment'}</span>
                    </div>
                  )}
                  <a href={msg.media_url} target="_blank" rel="noopener noreferrer" className="message-download-btn" title="Download">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download
                  </a>
                </div>
              ) : null}
              {msg.content && <p className="message-text">{msg.content}</p>}
              <div className="message-meta">
                <span className="message-time">{formatTime(msg.sent_at)}</span>
                {msg.direction === 'agent_to_user' && (
                  <span className={`message-status message-status-${msg.status}`}>
                    {msg.status === 'sending' ? '⏳' : msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓✓' : '✓'}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      {sendError && <div className="messages-send-error">{sendError}</div>}
      <div className="messages-compose">
        <textarea
          className="messages-input"
          placeholder={`Type a message... (Enter to send)`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={sending}
        />
        <button
          className="messages-send-btn"
          onClick={handleSend}
          disabled={!text.trim() || sending}
        >
          {sending ? (
            <span className="messages-send-spinner" />
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default MessagesPanel;
